/**
 * Centralized Insufficient Tokens Modal
 * Fullscreen overlay — Perplexity-style dark takeover
 *
 * - Guest users → "Sign up and get X free tokens" + "Need more tokens"
 * - Authenticated users → "Need more tokens"
 */

import { useEffect, useState } from "react";

import { t } from "@lingui/macro";
import { Sparkle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

import { axios } from "@/client/libs/axios";
import { usePaymentConfig } from "@/client/hooks/use-payment-config";
import { tokenConfigService } from "@/client/services/token/token-config.service";
import { useAuthStore } from "@/client/stores/auth";

type TokenPackageInfo = {
  name: string;
  tokens: number;
  price: number;
};

type InsufficientTokensModalProps = {
  isOpen: boolean;
  onClose: () => void;
  requiredTokens?: number;
  currentTokens?: number;
  featureName?: string;
  showUpgradeOption?: boolean;
  onNavigation?: () => void;
  onRetry?: () => void;
  isNetworkError?: boolean;
};

export const InsufficientTokensModal = ({
  isOpen,
  onClose,
  requiredTokens,
  currentTokens,
  onRetry,
  isNetworkError = false,
}: InsufficientTokensModalProps) => {
  const navigate = useNavigate();
  const { data: paymentConfig } = usePaymentConfig();
  const isLoggedIn = useAuthStore((s) => !!s.user);
  const freeTokens = tokenConfigService.getValueSync("REGISTERED_TOKEN_BALANCE");
  const [smallestPackage, setSmallestPackage] = useState<TokenPackageInfo | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const fetchSmallest = async () => {
      try {
        const res = await axios.get<TokenPackageInfo[]>("/payment/token-packages");
        if (cancelled || !res.data?.length) return;
        const sorted = [...res.data].sort((a, b) => a.price - b.price);
        setSmallestPackage({ name: sorted[0].name, tokens: sorted[0].tokens, price: sorted[0].price });
      } catch {
        // silent
      }
    };

    void fetchSmallest();
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSignUp = () => {
    onClose();
    void navigate("/auth/register");
  };

  const handleBuyTokens = () => {
    onClose();
    void navigate("/pricing?tab=tokens&autoCheckout=true");
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-[#0a0a0a]/95 backdrop-blur-sm">
      <button
        onClick={onClose}
        className="absolute right-6 top-6 text-white/20 hover:text-white/60 transition-colors"
        aria-label="Close"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      <div className="flex flex-col items-center text-center max-w-md w-full px-6">
        <h1 className="text-[28px] sm:text-[32px] font-semibold text-white/90 leading-tight tracking-tight">
          {isNetworkError
            ? t`Network Error`
            : !isLoggedIn
              ? t`Sign up to continue building`
              : t`Top up to continue`}
        </h1>

        <p className="mt-3 text-[16px] sm:text-[17px] text-white/35 leading-relaxed max-w-sm">
          {isNetworkError
            ? t`Please check your internet connection and try again.`
            : !isLoggedIn
              ? t`Create a free account to get ${freeTokens} free tokens`
              : t`You need more tokens for this action`}
        </p>

        {!isNetworkError && requiredTokens !== undefined && (
          <div className="mt-8 flex items-center gap-8">
            <div className="flex flex-col items-center">
              <span className="text-[11px] font-medium text-white/20 uppercase tracking-widest">{t`Balance`}</span>
              <span className="mt-1 text-2xl font-semibold text-white/50">{currentTokens ?? 0}</span>
            </div>
            <div className="h-10 w-px bg-white/[0.06]" />
            <div className="flex flex-col items-center">
              <span className="text-[11px] font-medium text-white/20 uppercase tracking-widest">{t`Needed`}</span>
              <span className="mt-1 text-2xl font-semibold text-white/50">{requiredTokens}</span>
            </div>
          </div>
        )}

        <div className="mt-10 flex w-full max-w-xs flex-col gap-3">
          {isNetworkError && onRetry ? (
            <button
              onClick={() => { onClose(); onRetry(); }}
              className="hero-cta-shimmer group relative flex h-14 w-full items-center justify-center gap-2.5 overflow-hidden rounded-full text-[16px] font-bold text-white shadow-lg"
              style={{
                background: "linear-gradient(90deg, #378491 0%, #df8357 100%)",
                boxShadow: "0 8px 24px rgba(55, 132, 145, 0.25), 0 4px 12px rgba(223, 131, 87, 0.2)",
              }}
            >
              <span className="hero-cta-shimmer-bg" aria-hidden />
              <span className="relative z-10">{t`Try Again`}</span>
            </button>
          ) : (
            <>
              {!isLoggedIn && (
                <button
                  onClick={handleSignUp}
                  className="hero-cta-shimmer group relative flex h-14 w-full items-center justify-center gap-2.5 overflow-hidden rounded-full text-[16px] font-bold text-white shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    background: "linear-gradient(90deg, #378491 0%, #df8357 100%)",
                    boxShadow: "0 8px 24px rgba(55, 132, 145, 0.25), 0 4px 12px rgba(223, 131, 87, 0.2)",
                  }}
                >
                  <span className="hero-cta-shimmer-bg" aria-hidden />
                  <Sparkle weight="fill" className="relative z-10 size-5 text-white" />
                  <span className="relative z-10">{t`Sign up and get ${freeTokens} free tokens`}</span>
                </button>
              )}

              {paymentConfig?.paymentsEnabled && (
                <button
                  onClick={handleBuyTokens}
                  className="group relative flex h-14 w-full flex-col items-center justify-center overflow-hidden rounded-full border border-white/[0.08] bg-white/[0.04] text-white/70 transition-all hover:bg-white/[0.08] hover:border-white/[0.12] hover:text-white/90 active:scale-[0.98]"
                >
                  <span className="text-[15px] font-semibold">{t`Need more tokens`}</span>
                  {smallestPackage && (
                    <span className="text-[11px] text-white/30 mt-0.5">
                      {t`Starting at ${smallestPackage.tokens} tokens for $${smallestPackage.price}`}
                    </span>
                  )}
                </button>
              )}
            </>
          )}
        </div>

        <button
          onClick={onClose}
          className="mt-8 text-[13px] text-white/20 hover:text-white/50 transition-colors"
        >
          {t`Close`}
        </button>
      </div>

      {/* Shimmer animation — reuses the same CSS from ResumeForm.css */}
      <style>{`
        .hero-cta-shimmer { position: relative; }
        .hero-cta-shimmer-bg {
          position: absolute; inset: 0;
          background: linear-gradient(110deg, transparent 0%, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%, transparent 100%);
          background-size: 200% 100%;
          animation: hero-cta-shimmer 3s ease-in-out infinite;
          pointer-events: none;
          border-radius: inherit;
        }
        @keyframes hero-cta-shimmer {
          0%, 100% { background-position: 200% 0; }
          50% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};
